import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboardingState } from '../../hooks/useOnboardingState';
import { UserTypeStep } from './steps/UserTypeStep';
import { LanguageSelectionStep } from './steps/LanguageSelectionStep';
import { CEFRLevelStep } from './steps/CEFRLevelStep';
import { RecommendationsStep } from './steps/RecommendationsStep';
import { TranslatorGuideStep } from './steps/TranslatorGuideStep';
import { ReaderGuideStep } from './steps/ReaderGuideStep';
import type { CEFRLevel, LibraryBook } from '../../types';

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const { t } = useTranslation('common');
  const { onboardingState, updateOnboarding, completeOnboarding } = useOnboardingState();
  const [currentStep, setCurrentStep] = useState(1);
  const [recommendations, setRecommendations] = useState<LibraryBook[]>([]);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [requestedLanguage, setRequestedLanguage] = useState<string>('');

  // Calculate total steps based on user type
  const getTotalSteps = () => {
    if (onboardingState.userType === 'learner') return 4;
    if (onboardingState.userType === 'translator') return 2;
    if (onboardingState.userType === 'reader') return 2;
    return 4; // default
  };

  const totalSteps = getTotalSteps();

  const goToNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleUserTypeSelect = (userType: 'learner' | 'translator' | 'reader') => {
    updateOnboarding({ userType });
    goToNext();
  };

  const handleLanguageSelect = (nativeLanguage: string, targetLanguage: string) => {
    updateOnboarding({ nativeLanguage, targetLanguage });
    goToNext();
  };

  const handleCEFRLevelSelect = (cefrLevel: CEFRLevel) => {
    updateOnboarding({ cefrLevel });
    // Automatically proceed to next step after selecting a level
    goToNext();
  };

  const handleCEFRLevelNext = () => {
    // Allow proceeding without selecting a level
    if (!onboardingState.cefrLevel) {
      updateOnboarding({ cefrLevel: 'A1' }); // Default to A1 if not selected
    }
    goToNext();
  };

  const handleComplete = () => {
    // Mark onboarding as completed (saves to localStorage immediately)
    completeOnboarding();
    // Close wizard and apply filters
    onComplete();
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  // Fetch recommendations when reaching the recommendations step (learner path)
  useEffect(() => {
    if (onboardingState.userType === 'learner' && currentStep === 4 && onboardingState.targetLanguage) {
      fetchRecommendations();
    }
  }, [currentStep, onboardingState.targetLanguage, onboardingState.userType]);

  const fetchRecommendations = async () => {
    try {
      const params = new URLSearchParams({
        target_language: onboardingState.targetLanguage || '',
        limit: '5',
      });

      const url = `/api/onboarding/recommendations?${params}`;
      console.log('Fetching recommendations from:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Recommendations data:', data);
        setRecommendations(data.books || []);
        setFallbackUsed(data.fallback_used || false);
        setRequestedLanguage(data.requested_language || '');
      } else {
        console.error('API returned error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  };

  return (
    <div className="onboarding-wizard fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="onboarding-content relative w-full h-[80vh] md:h-auto md:max-h-[90vh] max-w-2xl rounded-2xl md:rounded-lg bg-white flex flex-col">
        {/* Sticky Header */}
        <div className="onboarding-wizard-header flex-shrink-0 bg-white rounded-t-2xl md:rounded-t-lg p-4 md:p-6 border-b">
          <div className="onboarding-header mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {t('onboarding.welcome')}
            </h2>
            <p className="text-sm text-gray-600">
              {t('onboarding.stepOf', { current: currentStep, total: totalSteps })}
            </p>
          </div>

          {/* Progress bar */}
          <div className="progress-bar h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="progress-fill h-full bg-primary transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="step-content-wrapper flex-1 overflow-y-auto">
          <div className="step-content p-4 md:p-8 pb-20 md:pb-8">
          {/* Step 1: User Type Selection (All paths) */}
          {currentStep === 1 && (
            <UserTypeStep
              onSelect={handleUserTypeSelect}
              onBack={null}
              selectedType={onboardingState.userType}
            />
          )}

          {/* Learner Path Steps */}
          {onboardingState.userType === 'learner' && (
            <>
              {currentStep === 2 && (
                <LanguageSelectionStep
                  onSelect={handleLanguageSelect}
                  onBack={goToPrevious}
                  targetLanguage={onboardingState.targetLanguage}
                />
              )}

              {currentStep === 3 && (
                <CEFRLevelStep
                  onSelect={handleCEFRLevelSelect}
                  onNext={handleCEFRLevelNext}
                  onBack={goToPrevious}
                  selectedLevel={onboardingState.cefrLevel}
                />
              )}

              {currentStep === 4 && (
                <RecommendationsStep
                  recommendations={recommendations}
                  onComplete={handleComplete}
                  onBack={goToPrevious}
                  targetLanguage={onboardingState.targetLanguage || ''}
                  cefrLevel={onboardingState.cefrLevel || 'A1'}
                  fallbackUsed={fallbackUsed}
                  requestedLanguage={requestedLanguage}
                />
              )}
            </>
          )}

          {/* Translator Path Steps */}
          {onboardingState.userType === 'translator' && currentStep === 2 && (
            <TranslatorGuideStep
              onBack={goToPrevious}
              onComplete={handleComplete}
            />
          )}

          {/* Reader Path Steps */}
          {onboardingState.userType === 'reader' && currentStep === 2 && (
            <ReaderGuideStep
              onBack={goToPrevious}
              onComplete={handleComplete}
            />
          )}
          </div>
        </div>

        {/* Sticky Footer with Skip button */}
        {onSkip && (
          <div className="onboarding-wizard-footer flex-shrink-0 bg-white rounded-b-2xl md:rounded-b-lg p-4 border-t">
            <button
              onClick={handleSkip}
              className="skip-btn w-full py-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              {t('onboarding.skip')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

